/**
 * A drawn mark for each of the thirteen activity kinds.
 *
 * Generated materials have no image to show, and a wall of identical cards is
 * unreadable at a glance. Each mark is the shape of the activity itself — a
 * matrix has a missing cell, a memory game has one card face down — so the
 * Studio can be scanned rather than read.
 *
 * Everything is stroked in currentColor, which the card sets to the domain
 * hue, so a mark carries the same colour as the block it belongs to.
 */

import type { ReactElement } from 'react'

const S = {
  viewBox: '0 0 32 32',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  width: 32,
  height: 32,
  'aria-hidden': true,
}

const MARKS: Record<string, () => ReactElement> = {
  tangram: () => (
    <svg {...S}>
      <path d="M4 5h24v24H4z" opacity="0.28" />
      <path d="M4 5l12 12L4 29z" />
      <path d="M4 5h24L16 17z" />
      <path d="M28 5v12l-6-6z" opacity="0.55" />
    </svg>
  ),
  matrix_reasoning: () => (
    <svg {...S}>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="22" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
      <rect x="22" y="13" width="7" height="7" rx="1.5" />
      <rect x="4" y="22" width="7" height="7" rx="1.5" />
      <rect x="13" y="22" width="7" height="7" rx="1.5" />
      <rect x="22" y="22" width="7" height="7" rx="1.5" strokeDasharray="2.6 2.6" opacity="0.6" />
    </svg>
  ),
  sight_word_deck: () => (
    <svg {...S}>
      <rect x="3" y="9" width="19" height="15" rx="2.5" opacity="0.4" />
      <rect x="8" y="6" width="19" height="15" rx="2.5" />
      <path d="M13 13h9M13 16.5h6" />
    </svg>
  ),
  counting_game: () => (
    <svg {...S}>
      <circle cx="8" cy="9" r="3.2" />
      <circle cx="16" cy="9" r="3.2" />
      <circle cx="24" cy="9" r="3.2" opacity="0.45" />
      <path d="M8 20v8M13 20h6M13 24h6M13 28h6" opacity="0.75" />
    </svg>
  ),
  pattern_builder: () => (
    <svg {...S}>
      <circle cx="7" cy="16" r="4" />
      <rect x="12" y="12" width="8" height="8" rx="1.5" />
      <circle cx="25" cy="16" r="4" />
      <path d="M29 16h1" strokeDasharray="2 2" opacity="0.55" />
    </svg>
  ),
  deduction_puzzle: () => (
    <svg {...S}>
      <circle cx="14" cy="14" r="8.5" />
      <path d="M20.5 20.5L28 28" />
      <path d="M11 14h6" opacity="0.65" />
    </svg>
  ),
  sorting_game: () => (
    <svg {...S}>
      <path d="M4 17h9v10H4zM19 17h9v10h-9z" />
      <circle cx="8.5" cy="7" r="3" />
      <path d="M20.5 4h5l2.5 6h-10z" opacity="0.7" />
      <path d="M8.5 11v3M23.5 11v3" strokeDasharray="2 2.5" opacity="0.6" />
    </svg>
  ),
  sequence_order: () => (
    <svg {...S}>
      <rect x="4" y="6" width="24" height="6" rx="2" />
      <rect x="4" y="14" width="24" height="6" rx="2" opacity="0.6" />
      <rect x="4" y="22" width="24" height="6" rx="2" opacity="0.35" />
      <path d="M9 9h6M9 17h9M9 25h4" opacity="0.8" />
    </svg>
  ),
  letter_tracing: () => (
    <svg {...S}>
      <path d="M6 24c0-9 3-15 8-15s7 4 7 9-2 6-4 6" strokeDasharray="3 3" />
      <circle cx="6" cy="24" r="2" />
      <path d="M20 21l6 6-2.5 1-1-2.5z" opacity="0.8" />
    </svg>
  ),
  reading_timer: () => (
    <svg {...S}>
      <circle cx="16" cy="18" r="9.5" />
      <path d="M16 13v5l3.5 2.5" />
      <path d="M13 4h6M16 4v4.5" />
    </svg>
  ),
  memory_pairs: () => (
    <svg {...S}>
      <rect x="3" y="6" width="11" height="15" rx="2" />
      <rect x="18" y="11" width="11" height="15" rx="2" />
      <path d="M6.5 13.5h4M23.5 15v7M20 18.5h7" opacity="0.7" />
    </svg>
  ),
  model_3d: () => (
    <svg {...S}>
      <path d="M16 4l11 6v12l-11 6-11-6V10z" />
      <path d="M16 16l11-6M16 16v12M16 16L5 10" opacity="0.55" />
    </svg>
  ),
  story_builder: () => (
    <svg {...S}>
      <path d="M16 9c-2.5-3-6-3.5-11-3v18c5-.5 8.5 0 11 3 2.5-3 6-3.5 11-3V6c-5-.5-8.5 0-11 3z" />
      <path d="M16 9v18" opacity="0.5" />
      <path d="M22 3l.9 2.4L25 6.5l-2.1.9L22 10l-.9-2.6L19 6.5l2.1-1.1z" opacity="0.85" />
    </svg>
  ),
}

/** Falls back to a generic spark so an unfamiliar kind still gets a face. */
export function KindMark({ kind }: { kind?: string }) {
  const Mark = (kind && MARKS[kind]) || Fallback
  return <Mark />
}

function Fallback() {
  return (
    <svg {...S}>
      <path d="M16 5l2.8 7.6L26 16l-7.2 3.4L16 27l-2.8-7.6L6 16l7.2-3.4z" />
    </svg>
  )
}
